import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Link } from 'react-router-dom'

interface ErrorPageProps {
  error?: Error
  onReset?: () => void
}

export function ErrorPage({ error, onReset }: ErrorPageProps) {
  const handleReload = () => {
    if (onReset) {
      onReset()
    } else {
      window.location.reload()
    }
  }

  const handleGoHome = () => {
    window.location.href = '/'
  }

  return (
    <div className="h-screen bg-background flex items-center justify-center overflow-hidden">
      <Card className="w-full max-w-md mx-4 p-6 text-center space-y-4">
        {/* Logo da Ephemeral */}
        <div className="flex justify-center">
          <img 
            src="https://fqgglbhawczmdkkhhtnz.supabase.co/storage/v1/object/public/public_assets/ui/logos/side_logo.png" 
            alt="Ephemeral Cards" 
            className="h-12 dark:filter dark:brightness-0 dark:invert"
          />
        </div>

        {/* Ícone de erro */}
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-3">
            <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
        </div>

        {/* Título e descrição */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Oops! Algo deu errado
          </h1>
          <p className="text-muted-foreground">
            Encontramos um problema inesperado. Não se preocupe, isso acontece às vezes.
          </p>
        </div>

        {/* Detalhes do erro (apenas em desenvolvimento) */}
        {import.meta.env.DEV && error && (
          <details className="text-left bg-muted p-4 rounded-lg">
            <summary className="cursor-pointer font-medium text-sm">
              Detalhes técnicos (desenvolvimento)
            </summary>
            <pre className="mt-2 text-xs text-muted-foreground overflow-auto">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        {/* Botões de ação */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={handleReload}
            className="flex-1 h-14 text-base font-semibold px-6 py-4"
            size="lg"
          >
            <RefreshCw className="h-5 w-5 mr-2" />
            Recarregar Página
          </Button>
          
          <Button 
            onClick={handleGoHome}
            variant="outline"
            className="flex-1 h-14 text-base font-semibold px-6 py-4"
            size="lg"
          >
            <Home className="h-5 w-5 mr-2" />
            Ir para Início
          </Button>
        </div>

        {/* Link para suporte */}
        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Se o problema persistir, entre em contato conosco através do{' '}
            <Link 
              to="/community" 
              className="text-primary hover:underline"
            >
              chat da comunidade
            </Link>
          </p>
        </div>
      </Card>
    </div>
  )
}
